/**
 * AccionesInforme — emitir el informe de UNA compañía: descargar, guardar o enviar.
 *
 * VA POR COMPAÑÍA Y NO EN LA BARRA DE ARRIBA. El PDF es un documento dirigido a UNA
 * aseguradora; con «Todas» seleccionado, una acción global tendría que elegir destinatario
 * por su cuenta. Así cada documento lleva sus propios botones y se emite el que se mira.
 *
 * ENVIAR PIDE CONFIRMACIÓN, las otras dos no. Descargar y guardar se deshacen —el archivo
 * se borra, la fila se reemplaza—; un correo a una aseguradora no. La confirmación dice el
 * destinatario exacto, que sale del mandato de recaudación y no de quien aprieta el botón.
 *
 * NO SE IMPRIME DESDE ACÁ. El PDF lo genera Playwright en el servidor; `window.print()`
 * dependía del navegador del operador y no dejaba registro en `informes_accesos`.
 *
 * @see src/services/yoolpay/informePdfApi.js
 * @see api/pdf/generate.js
 */
import React, { useCallback, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Download, Mail, Save } from 'lucide-react';

import { Boton, Modal } from '../cierre/ds';
import { Aviso } from '../cierre/piezas';
import {
  descargarInformePdf,
  enviarInformePdf,
  guardarInformePdf,
} from '../../../../services/yoolpay/informePdfApi';

export default function AccionesInforme({ informe, tipo, fecha, period }) {
  const { getToken } = useAuth();
  const [ocupado, setOcupado] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [confirmando, setConfirmando] = useState(false);

  const params = { tipo, fecha, period, compania: informe.compania_id };
  const destinatario = informe.destino?.girable ? informe.destino.destino.email : null;

  const correr = useCallback(async (clave, fn) => {
    setOcupado(clave);
    setError(null);
    setAviso(null);
    try {
      const token = await getToken();
      return await fn(token);
    } catch (e) {
      setError(e?.message ?? 'No se pudo emitir el informe');
      return null;
    } finally {
      setOcupado(null);
    }
  }, [getToken]);

  const descargar = () => correr(
    'descargar',
    (t) => descargarInformePdf(t, params, `${informe.numero}.pdf`),
  );

  const guardar = () => correr('guardar', async (t) => {
    const r = await guardarInformePdf(t, params);
    if (r?.ok) setAviso(`Guardado en settlement/informes · archivo ${r.archivo_id}`);
    return r;
  });

  const enviar = () => correr('enviar', async (t) => {
    const r = await enviarInformePdf(t, params);
    if (r?.ok) setAviso(`Enviado a ${r.email_sent_to}`);
    setConfirmando(false);
    return r;
  });

  return (
    <div style={{ width: '210mm', maxWidth: '100%', margin: '0 auto 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--yool-gray1)', marginRight: 'auto' }}>
          {informe.numero} · {informe.compania_nombre ?? 'sin compañía'}
        </span>
        <Boton variant="primary" onClick={descargar} disabled={ocupado != null}>
          <Download className="h-4 w-4" />
          {ocupado === 'descargar' ? ' Generando…' : ' Descargar PDF'}
        </Boton>
        <Boton variant="outline" onClick={guardar} disabled={ocupado != null}>
          <Save className="h-4 w-4" />
          {ocupado === 'guardar' ? ' Guardando…' : ' Guardar'}
        </Boton>
        {/* SIN CORREO REGISTRADO NO SE HABILITA. El destinatario sale del mandato de
            recaudación; pedirlo a mano acá abriría la puerta a mandarle el informe de una
            compañía a otra dirección. */}
        <Boton
          variant="outline"
          onClick={() => setConfirmando(true)}
          disabled={ocupado != null || !destinatario}
          title={destinatario
            ? `Enviar a ${destinatario}`
            : 'La compañía no tiene correo de aviso en sus datos bancarios'}
        >
          <Mail className="h-4 w-4" />
          {' '}Enviar a la compañía
        </Boton>
      </div>

      {error ? (
        <div style={{ marginTop: 8 }}>
          <Aviso tono="alta" titulo="No se pudo emitir">{error}</Aviso>
        </div>
      ) : null}
      {aviso ? (
        <div style={{ marginTop: 8 }}>
          <Aviso tono="baja" titulo="Listo">{aviso}</Aviso>
        </div>
      ) : null}

      {confirmando ? (
        <Modal
          open
          onClose={() => setConfirmando(false)}
          eyebrow="Sale de Yool"
          title="Enviar el informe a la compañía"
          desc={`El PDF ${informe.numero} se manda a ${destinatario}, el correo de aviso `
            + 'registrado en el mandato de recaudación. Un correo enviado no se puede '
            + 'deshacer.'}
        >
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Boton variant="ghost" onClick={() => setConfirmando(false)}>Cancelar</Boton>
            <Boton variant="primary" onClick={enviar} disabled={ocupado === 'enviar'}>
              {ocupado === 'enviar' ? 'Enviando…' : 'Enviar'}
            </Boton>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
